import { Helmet } from "react-helmet-async";
import seoConfig from "@/config/seo-config.json";
import { useLanguage } from "@/i18n/LanguageContext";

// iter643 — Supported language alternates for hreflang tags.
// Spanish was added in iter642; bg remains default for x-default.
const HREFLANG_LANGS = [
  { code: "bg", ogLocale: "bg_BG" },
  { code: "en", ogLocale: "en_US" },
  { code: "es", ogLocale: "es_ES" },
];

export default function SEO({ title, description, path, noindex, keywords }) {
  const { language } = useLanguage();
  const siteName = seoConfig.siteName || "Nutramilo";
  const siteUrl = seoConfig.siteUrl || "https://nutramilo.com";
  const pageConfig = path ? seoConfig.pages?.[path] : null;

  // Priority: seo-config.json (admin-managed) > component props > defaults
  const configTitle = pageConfig?.title;
  const configDesc = pageConfig?.description;

  const finalTitle = configTitle
    || (title ? (title.includes(siteName) ? title : `${title} | ${siteName}`) : null)
    || seoConfig.defaults?.title
    || `${siteName} — AI платформа за хранене и метаболизъм`;

  const finalDescription = configDesc
    || description
    || seoConfig.defaults?.description
    || "";

  const finalKeywords = pageConfig?.keywords || keywords || seoConfig.defaults?.keywords || "";
  const basePath = path || "";
  // iter673 — canonical URL must NOT include the ?lang= param. The sitemap
  // declares the lang-less URL as the canonical form; hreflang alternates
  // below carry the per-language variants. Including ?lang= here caused
  // "Duplicate, Google chose different canonical" warnings in GSC.
  const canonicalUrl = `${siteUrl}${basePath}`;
  const ogImage = `${siteUrl}${seoConfig.ogImage || "/app-icon.png"}`;

  // iter643 — hreflang alternates so Google can serve the right language per locale.
  const buildLangUrl = (lang) => `${siteUrl}${basePath}${basePath.includes("?") ? "&" : "?"}lang=${lang}`;
  const currentOgLocale =
    HREFLANG_LANGS.find((l) => l.code === language)?.ogLocale || "bg_BG";

  return (
    <Helmet>
      <html lang={language || "bg"} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={seoConfig.author || ""} />
      <link rel="canonical" href={canonicalUrl} />

      {/* iter643 — hreflang alternates (BG/EN/ES + x-default) */}
      {HREFLANG_LANGS.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={buildLangUrl(l.code)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${basePath}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={currentOgLocale} />
      {HREFLANG_LANGS.filter((l) => l.ogLocale !== currentOgLocale).map((l) => (
        <meta key={l.code} property="og:locale:alternate" content={l.ogLocale} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Structured Data */}
      {seoConfig.structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(seoConfig.structuredData)}
        </script>
      )}
    </Helmet>
  );
}
